import { Component, Element, Event, EventEmitter, Method, Prop, State, Listen, Watch, h, Host } from '@stencil/core';
import { whichTransitionEvent } from '../../utils/functions';
import { clearAllBodyScrollLocks, disableBodyScroll } from '../../utils/bodyScrollLock';

/**
 * Panels are partial screen overlays that interrupt user interaction and demand an action. They are appropriate when user interaction is temporary and needed only to perform a specific action such as filtering.
 */
@Component({
  tag: 'som-panel',
  styleUrl: 'som-panel.scss',
})
export class SomPanel {
  @Element() el: HTMLElement;

  /**
   * This boolean property is used to display or hide the panel on initial load.
   */
  @Prop({ mutable: true }) shown: boolean = false;

  /**
   * This property is where the panel should appear.
   */
  @Prop({ mutable: true }) position: 'left' | 'right' = 'left';

    /**
   * This property is used to control the panel’s width. The sizes are as follows: `sm = 20rem`, `md = 30rem`, `lg = 40rem`, `xl = fullscreen - 38px`
   */
  @Prop({ mutable: true }) size: 'sm' | 'md' | 'lg' = 'md';

  /**
   * This property is where the mobile navigation should take effect.
   */
  @Prop({ mutable: true }) breakpoint: 'sm' | 'md' | 'lg' | 'xl' = 'lg';

  /**
   * This boolean property is used to hide the panel's close button
   */
  @Prop() hideCloseButton: boolean = false;

  //! Would like to remove this prop - only used in header
  /**
   * This boolean property is used to determine if the panel should show over or below header.
   */
  @Prop() belowHeader: boolean = false;

  /**
   * State Variables
   */
  @State() preDisplay: boolean = false;

  @State() opened: boolean = false;

  @State() animating: boolean = false;

  /**
   * This event is triggered just before panel is opened.
   */
  @Event() beforeOpen: EventEmitter;

  /**
   * This event is triggered right after panel is opened.
   */
  @Event() afterOpen: EventEmitter;

  /**
   * This event is triggered just before panel is closed.
   */
  @Event() beforeClose: EventEmitter;

  /**
   * This event is triggered right after panel is closed.
   */
  @Event() afterClose: EventEmitter;

  elemWithAnimation;
  panelContainer;
  panelDialog;
  panelDidOpenCb;
  panelDidCloseCb;
  panelCloseBtn;

  // Focus & tabbable elements
  focusedElBeforeOpen;
  focusableEls = [];
  firstFocusableEl;
  lastFocusableEl;

  @Watch('shown')
  shownChanged(newValue) {
    if (newValue) {
      this.open();
    } else {
      this.close();
    }
  }

  handleOverlayClick(e) {
    // Close panel when a click occurs on the overlay, outside the modal dimensions
    if (e.target.contains(this.panelDialog)) {
      this.close();
    }
  }

  /**
   * Listen for menu parent list item to close so we can appropriately handle body scroll locking
   */
  @Listen('listClosed', { target: 'window' })
  handleListClose() {
    clearAllBodyScrollLocks();
    disableBodyScroll(this.panelContainer);
  }

  /**
   * This method will open the panel.
   * @returns Promise will resolve when the open animation ends.
   */
  @Method()
  open(): Promise<void> {
    return new Promise(async resolve => {
      if (!this.opened ) {
        // scroll back up to the top of the page
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;

        this.panelDialog.showModal();
        this.opened = false;

        // Make sure scolling on body is disabled (allow scrolling only on the panel element)
        disableBodyScroll(this.panelContainer);

        await this.refresh();

        this.preDisplay = true;
        // Wait for open animation to finish before sending event and resolving
        this.listenForTransitionEnd(this.elemWithAnimation, () => {
          this.panelDidOpen();
          resolve();
       });
      } else {
        // Panel is already opened
        resolve();
      }
    });
  }

  /**
   * This method will close the panel.
   * @returns Promise will resolve when the close animation ends.
   */
  @Method()
  close(): Promise<void> {
    return new Promise(resolve => {
      if (this.opened) {
        // Enable body scrolling back
        clearAllBodyScrollLocks();

        // Triggers that the modal will close
        this.beforeClose.emit();

        this.panelDialog.close();

        this.animating = true;
        this.opened = false;

        // Wait for open animation to finish before sending event and resolving
        this.listenForTransitionEnd(this.elemWithAnimation, () => {
          this.panelDidClose();
          resolve();
        });
      } else {
        // Panel is already closed
        resolve();
      }
    });
  }

  /**
   * This method will refresh the panel. This can be used when loading asynchronous data, which allows the component to find focusable elements and provide optimal accessibility support.
   * @returns Promise will resolve after the refresh.
   */
  @Method()
  refresh(): Promise<void> {
    return new Promise(async resolve => {
      // Refresh focusable elements
      //this.refreshFocusableElements();

      // Make sure focus is set to the modal after refresh
      // const currentlyFocusedElement = find(this.focusableEls, elem => elem === document.activeElement);
      // if (!currentlyFocusedElement) {
      //   await focusElement(this.panelContainer);
      // }

      resolve();
    });
  }

  openPanel() {
    this.animating = true;
    this.opened = true;
  }

  async panelDidOpen() {
    this.animating = false;

    // Triggers that the modal is opened
    this.afterOpen.emit();
  }

  async panelDidClose() {
    this.animating = false;

    // Triggers that the modal is closed
    this.afterClose.emit();
  }

  listenForTransitionEnd(elem, callback) {
    const transitionEvent = whichTransitionEvent();
    let onTransitionCompletedCb;
    const onTransitionCompleted = e => {
      // Make sure transition happened directly on container and not inside
      if (e.target === elem) {
        elem.removeEventListener(transitionEvent, onTransitionCompletedCb);
        // Transition is completed
        callback();
      }
    };
    onTransitionCompletedCb = onTransitionCompleted.bind(this);
    if (transitionEvent) {
      elem.addEventListener(transitionEvent, onTransitionCompletedCb);
    }
  }

  handlePanelScroll(event) {
    // There is no close button so don't run this code.
    if (this.hideCloseButton) return true;

    const e = event.target;    
    if (e.scrollTop) {
      this.panelCloseBtn.classList.add('scrolling');
    } else {
      this.panelCloseBtn.classList.remove('scrolling');
    }
  }

  componentDidUpdate() {
    if (this.preDisplay) {
      // Panel has been displayed (display: block). Let's move it!
      setTimeout(() => {
        this.openPanel();
        this.preDisplay = false;
      }, 0);
    }
  }

  componentWillLoad() {
    this.panelDidOpenCb = this.panelDidOpen.bind(this);
    this.panelDidCloseCb = this.panelDidClose.bind(this);

    this.opened = this.shown;
  }

  componentDidLoad() {
    this.elemWithAnimation = this.el.querySelector('.panel');

    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.panelDialog.addEventListener('click', this.handleOverlayClick);

    this.panelDialog.addEventListener("close", () => {
      this.close();
    });

    this.handlePanelScroll = this.handlePanelScroll.bind(this);
    this.panelDialog.addEventListener('scroll', this.handlePanelScroll);
  }

  disconnectedCallback() {
    // Make sure to reenable body scroll in case it was previously deactivated
    clearAllBodyScrollLocks();
  }

  render() {

    // Add tabindex to <Host /> so that when a click occurs on the panel body, it gets focus
    return (
      <Host class={`panel-container`}>    
        <dialog class={`panel-breakpoint-${this.breakpoint} panel panel-${this.position} panel-size-${this.size} ${this.opened ? 'opened' : ''} ${!this.hideCloseButton ? 'panel-close-button' : ''} ${this.belowHeader ? 'below-header' : ''}`} ref={(el) => this.panelDialog = el}>
            {!this.hideCloseButton ? (
              <button class="close--button" ref={(el) => this.panelCloseBtn = el} onClick={() => this.close()} aria-label="Close modal">
                <span aria-hidden="true">
                  <som-icon name="close"></som-icon>
                </span>
              </button>
            ) : null}
            <div class="panel-content" ref={(el) => this.panelContainer = el}>
              <slot></slot>
            </div>
        </dialog>
      </Host>
    );
  }
}
